import createClient from 'openapi-fetch';

import { resolveBaseUrl } from './baseUrl';
import type { paths } from './schema';
import { getAccessToken } from '@/auth/accessTokenStore';
import { getSessionUser, refreshSession, sessionIdentity } from '@/auth/session';

/**
 * The single typed entry point to the backend.
 *
 * Every request in the application goes through this client, which is generated from
 * api/openapi.yaml. If a controller signature changes without the contract being regenerated,
 * compilation fails here rather than at runtime in a browser.
 *
 * Requests are same-origin by default: the Vite dev server and the production reverse proxy both
 * mount the API under /api.
 */

export { resolveBaseUrl };

/** Paths that must never trigger a refresh-and-retry, because they are the refresh machinery. */
const AUTH_PATHS = ['/api/auth/login', '/api/auth/refresh', '/api/auth/logout'];

/**
 * The single in-flight refresh now lives in `@/auth/session`, so the provider's mount-time resume
 * shares it with these retries instead of racing them. See `refreshSession` there.
 */

function isAuthPath(url: string): boolean {
  const { pathname } = new URL(url);
  return AUTH_PATHS.includes(pathname);
}

function withAuthorization(request: Request): Request {
  const token = getAccessToken();
  if (token === null) {
    return request;
  }
  const authorised = new Request(request);
  authorised.headers.set('Authorization', `Bearer ${token}`);
  return authorised;
}

/**
 * Sends a request with the current access token, and recovers once from an expired one.
 *
 * Access tokens are deliberately short-lived, so an expiry mid-session is ordinary rather than
 * exceptional. Handling it here means no screen has to think about it.
 *
 * @param request the request the typed client built
 * @returns the response, after at most one transparent retry
 */
export async function fetchWithSession(request: Request): Promise<Response> {
  // Cloned before the first send: a Request body can only be consumed once, and a retry needs
  // its own copy.
  const retryable = request.clone();
  // Whose request this is. A retry is only ever a retry FOR THIS PRINCIPAL.
  const sentAs = sessionIdentity(getSessionUser());
  const response = await globalThis.fetch(withAuthorization(request));

  if (response.status !== 401 || isAuthPath(request.url)) {
    return response;
  }

  const refreshed = await refreshSession();
  if (refreshed === null) {
    return response;
  }
  // The refresh may have resumed a DIFFERENT principal — another tab signed in as somebody else
  // against the same cookie. Replaying here would send this user's pending write with that user's
  // token, into that user's workspace. The 401 is the honest answer; the interface re-resolves
  // from the published session change.
  if (sentAs !== null && sessionIdentity(refreshed) !== sentAs) {
    return response;
  }
  return globalThis.fetch(withAuthorization(retryable));
}

export const apiClient = createClient<paths>({
  baseUrl: resolveBaseUrl(),
  fetch: fetchWithSession,
});

export type PingResponse =
  paths['/api/ping']['get']['responses']['200']['content']['application/json'];

/** Thrown when the backend answers with a non-2xx status. */
export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/**
 * Calls the platform probe endpoint.
 *
 * @returns the probe payload exactly as the OpenAPI contract describes it
 * @throws ApiError when the backend reports a failure
 */
export async function fetchPing(): Promise<PingResponse> {
  const { data, response } = await apiClient.GET('/api/ping');

  if (!response.ok || data === undefined) {
    throw new ApiError(response.status, `Ping failed with status ${String(response.status)}`);
  }

  return data;
}

/** The workspace's audit events, newest first. */
export type AuditEvent =
  paths['/api/audit-events']['get']['responses']['200']['content']['application/json'][number];

/**
 * Reads the workspace's change log.
 *
 * @param size how many events to fetch
 * @returns the events, newest first
 * @throws ApiError when the caller may not read the trail, or the backend fails
 */
export async function fetchAuditEvents(size = 25): Promise<AuditEvent[]> {
  const { data, response } = await apiClient.GET('/api/audit-events', {
    params: { query: { size } },
  });

  if (!response.ok || data === undefined) {
    throw new ApiError(
      response.status,
      `Could not read the audit trail (${String(response.status)})`,
    );
  }

  return data;
}
